import { useQuery } from '@tanstack/react-query'
import { CircleAlert, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert } from '@/components/feedback/Alert'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { StatCard } from '@/components/stat/StatCard'
import { CashDrillPanel, type CashCell } from '@/features/cash/CashDrillPanel'
import { DecisionSummary } from '@/features/cash/DecisionSummary'
import { useCashFirm } from '@/features/cash/hooks'
import { CashFirmSelect, CashStrip } from '@/features/cash/shared'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { useQueryState } from '@/hooks/use-query-state'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import {
  cashRowLabel,
  cashSections,
  cashTone,
  formatCashAmount,
  formatCashCell,
  formatCashExact,
  isDrillable,
  periodLabel,
  type CashTone,
} from '@/lib/cash'
import { periodStatuses, scenarioGap, stripPoints, summarizeWeekly } from '@/lib/cash-summary'
import { formatCubeDateTime, formatDateOnly } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { cashApi, type CashRow, type CashWeekly } from '@/services/http/cash'

const text = messages.cash

const TONE_CLASS: Record<Exclude<CashTone, undefined>, string> = {
  negative: 'text-danger',
  belowMin: 'text-warning',
}

/** Seçili hafta sütununun vurgusu (nakit şeridinden tıklanır). */
const HIGHLIGHT_CLASS = 'bg-brand-subtle'

/** Sabit ilk sütun dar ekranda daralır; uzun kalem adları alt satıra sarılır. */
const FIRST_COLUMN = 'w-36 min-w-36 sm:w-72 sm:min-w-72'

const weekHeaderId = (end: string) => `cash-week-${end}`

function NumberCell({
  row,
  value,
  period,
  weekly,
  highlight,
  onOpen,
}: {
  row: CashRow
  value: number
  period: number
  weekly: CashWeekly
  highlight: boolean
  onOpen: (cell: CashCell) => void
}) {
  const tone = cashTone(row, value, weekly.min_cash)
  const className = cn(
    'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
    tone && TONE_CLASS[tone],
    Math.abs(value) < 0.005 && 'text-text-muted',
    highlight && HIGHLIGHT_CLASS,
  )
  if (!isDrillable(row) || Math.abs(value) < 0.005) {
    return (
      <td
        className={className}
        title={formatCashExact(value)}
        data-highlight={highlight || undefined}
      >
        {formatCashCell(value)}
      </td>
    )
  }
  const label = periodLabel(weekly.periods[period]!)
  return (
    <td className={cn('p-0', highlight && HIGHLIGHT_CLASS)} data-highlight={highlight || undefined}>
      <button
        type="button"
        className={cn(
          className,
          'hover:bg-surface-muted focus-visible:ring-ring w-full cursor-pointer rounded-sm outline-none focus-visible:ring-2',
        )}
        title={formatCashExact(value)}
        aria-label={`${cashRowLabel(row.key)}, ${label.title}: ${formatCashExact(value)}`}
        onClick={() => onOpen({ row: row.key, period })}
      >
        {formatCashCell(value)}
      </button>
    </td>
  )
}

/** Eksi / eşik altı sütun başlığında renge ek görünür metin ve simge. */
function PeriodFlag({ status }: { status: 'negative' | 'belowMin' }) {
  const negative = status === 'negative'
  const Icon = negative ? CircleAlert : TriangleAlert
  return (
    <span
      className={cn(
        'flex items-center justify-end gap-1 text-xs font-medium',
        negative ? 'text-danger' : 'text-warning',
      )}
    >
      <Icon aria-hidden className="size-3" />
      {negative ? text.weekly.negativeShort : text.weekly.belowMinShort}
    </span>
  )
}

function CashTable({
  weekly,
  highlightEnd,
  onOpen,
}: {
  weekly: CashWeekly
  highlightEnd: string | null
  onOpen: (cell: CashCell) => void
}) {
  const statuses = periodStatuses(weekly)
  return (
    <div className="bg-surface overflow-x-auto rounded-lg border">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              scope="col"
              className={cn(
                'bg-surface sticky left-0 z-10 border-b px-3 py-2 text-left font-medium',
                FIRST_COLUMN,
              )}
            >
              {text.weekly.rowHeader}
            </th>
            {weekly.periods.map((period, i) => {
              const label = periodLabel(period)
              const status = statuses[i]
              return (
                <th
                  key={period.start}
                  id={weekHeaderId(period.end)}
                  scope="col"
                  className={cn(
                    'border-b px-3 py-2 text-right font-medium whitespace-nowrap',
                    period.kind === 'ay' && 'bg-surface-subtle',
                    status === 'negative' && 'text-danger',
                    period.end === highlightEnd && HIGHLIGHT_CLASS,
                  )}
                  title={
                    status === 'negative'
                      ? text.weekly.negative
                      : status === 'belowMin'
                        ? text.weekly.belowMin
                        : undefined
                  }
                  data-negative={status === 'negative' || undefined}
                  data-highlight={period.end === highlightEnd || undefined}
                >
                  <span className="block">{label.title}</span>
                  <span className="text-text-secondary block text-xs font-normal">
                    {label.range}
                  </span>
                  {status && <PeriodFlag status={status} />}
                </th>
              )
            })}
            <th scope="col" className="border-b px-3 py-2 text-right font-medium">
              {text.weekly.total}
            </th>
          </tr>
        </thead>
        {cashSections(weekly.rows).map((section) => (
          <tbody key={section.key}>
            <tr>
              <th
                scope="rowgroup"
                colSpan={weekly.periods.length + 2}
                className="bg-surface-muted text-text-secondary border-b px-3 py-1.5 text-left text-xs font-semibold tracking-wide uppercase"
              >
                {text.sections[section.key]}
              </th>
            </tr>
            {section.rows.map((row) => {
              const strong = row.kind !== 'flow'
              return (
                <tr key={row.key} className={cn('border-b', strong && 'font-semibold')}>
                  <th
                    scope="row"
                    className={cn(
                      'bg-surface sticky left-0 z-10 border-b px-3 py-1.5 text-left font-[inherit]',
                      FIRST_COLUMN,
                    )}
                  >
                    {cashRowLabel(row.key)}
                  </th>
                  {row.values.map((value, i) => (
                    <NumberCell
                      key={weekly.periods[i]!.start}
                      row={row}
                      value={value}
                      period={i}
                      weekly={weekly}
                      highlight={weekly.periods[i]!.end === highlightEnd}
                      onOpen={onOpen}
                    />
                  ))}
                  <td className="border-b px-3 py-1.5 text-right whitespace-nowrap tabular-nums">
                    {row.total === null ? '' : formatCashCell(row.total)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        ))}
      </table>
    </div>
  )
}

function Position({ weekly }: { weekly: CashWeekly }) {
  const p = weekly.position
  const t = text.position
  return (
    <section aria-label={t.title} className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{t.title}</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label={t.bank} value={formatCashAmount(p.bank)} animate={false} />
        <StatCard label={t.cash} value={formatCashAmount(p.cash)} animate={false} />
        <StatCard label={t.portfolio} value={formatCashAmount(p.portfolio)} animate={false} />
        <StatCard
          label={t.inCollection}
          value={formatCashAmount(p.in_collection)}
          animate={false}
        />
        <StatCard
          label={t.total}
          value={formatCashAmount(p.total)}
          valueClassName={p.total < 0 ? 'text-danger' : undefined}
          animate={false}
        />
      </div>
    </section>
  )
}

/** "Plana alınmayan" satırı (plan G4): nakde girmeyen bakiyeler, Veri Sağlığı'na bağlanır. */
function Unplanned({ weekly }: { weekly: CashWeekly }) {
  const u = text.unplanned
  if (!weekly.unplanned.length) return null
  return (
    <section aria-label={u.title} className="bg-surface flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{u.title}</h2>
        <Link
          to={`/nakit/veri-sagligi?firma=${encodeURIComponent(weekly.firm)}`}
          className="text-sm underline"
        >
          {u.link}
        </Link>
      </div>
      <ul className="grid gap-1 text-sm sm:grid-cols-2">
        {weekly.unplanned.map((row) => (
          <li key={row.kind} className="flex justify-between gap-3" data-kind={row.kind}>
            <span>{u.kinds[row.kind] ?? row.kind}</span>
            <span className="tabular-nums">{u.row(row.count, formatCashAmount(row.amount))}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** /nakit/haftalik — Haftalık Nakit (docs/NAKIT-PLANI-PLAN.md Faz 1, salt okunur). */
export function WeeklyCashPage() {
  const [cell, setCell] = useState<CashCell | null>(null)
  const { firms, firm, setFirm } = useCashFirm({ allowGroup: true })
  const urlState = useQueryState()
  const scenario = urlState.get('senaryo') === 'kotumser' ? 'kotumser' : 'baz'
  const weekly = useQuery({
    queryKey: queryKeys.cash.weekly(firm ?? '', scenario),
    queryFn: () => cashApi.weekly(firm!, scenario),
    enabled: !!firm,
    placeholderData: (previous) => previous,
  })
  const data = weekly.data
  // Öbür senaryo yalnız kötümser − baz farkı için çekilir; gelmezse fark satırı görünmez.
  const otherScenario = scenario === 'baz' ? 'kotumser' : 'baz'
  const other = useQuery({
    queryKey: queryKeys.cash.weekly(firm ?? '', otherScenario),
    queryFn: () => cashApi.weekly(firm!, otherScenario),
    enabled: !!firm && weekly.isSuccess,
  })
  const [highlightEnd, setHighlightEnd] = useState<string | null>(null)
  const showWeek = (end: string) => {
    setHighlightEnd(end)
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    document.getElementById(`cash-week-${end}`)?.scrollIntoView?.({
      behavior: reduce ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }
  const summary = data && summarizeWeekly(data)
  const gap =
    data && other.data && summary
      ? scenarioGap(
          summarizeWeekly(scenario === 'baz' ? data : other.data),
          summarizeWeekly(scenario === 'baz' ? other.data : data),
        )
      : null

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={text.weekly.title}
        description={text.weekly.description}
        actions={
          <>
            <OptionSelect
              value={scenario}
              options={['baz', 'kotumser'].map((v) => ({
                value: v,
                label: text.weekly.scenarios[v]!,
              }))}
              onValueChange={(v) => {
                setCell(null)
                setHighlightEnd(null)
                urlState.set({ senaryo: v === 'baz' ? undefined : v })
              }}
              aria-label={text.weekly.scenario}
              className="w-36"
            />
            <CashFirmSelect
              firms={firms.data ?? []}
              firm={firm}
              onChange={(value) => {
                setCell(null)
                setHighlightEnd(null)
                setFirm(value)
              }}
            />
          </>
        }
      />

      {firms.isError ? (
        <ErrorState error={firms.error} onRetry={() => void firms.refetch()} />
      ) : firms.isSuccess && (firms.data ?? []).length === 0 ? (
        <EmptyState title={text.weekly.noFirms} description={text.weekly.noFirmsHint} />
      ) : weekly.isError ? (
        <ErrorState error={weekly.error} onRetry={() => void weekly.refetch()} />
      ) : !data ? (
        <Loading rows={8} />
      ) : (
        <>
          <p className="text-text-secondary flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>{text.weekly.start(formatDateOnly(data.start))}</span>
            {data.cari_snapshot_at && (
              <span>{text.weekly.cariSnapshot(formatCubeDateTime(data.cari_snapshot_at))}</span>
            )}
            {data.bank_snapshot_at && (
              <span>{text.weekly.bankSnapshot(formatCubeDateTime(data.bank_snapshot_at))}</span>
            )}
            {data.fx_date && <span>{text.weekly.fxDate(formatDateOnly(data.fx_date))}</span>}
          </p>
          {data.scenario === 'kotumser' && <Alert tone="info" title={text.weekly.scenarioHint} />}
          {data.warnings.map((w) => (
            <Alert
              key={`${w.code}-${w.detail}`}
              tone="warning"
              title={text.warnings[w.code](w.detail)}
            />
          ))}
          {summary && <DecisionSummary summary={summary} gap={gap} />}
          <CashStrip
            points={stripPoints(data)}
            minCash={data.min_cash}
            selectedEnd={highlightEnd}
            onSelect={(point) => showWeek(point.end)}
          />
          <Position weekly={data} />
          <CashTable weekly={data} highlightEnd={highlightEnd} onOpen={setCell} />
          <Unplanned weekly={data} />
          <CashDrillPanel weekly={data} cell={cell} onClose={() => setCell(null)} />
        </>
      )}
    </div>
  )
}
